import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  input,
  model,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, of } from 'rxjs';
import { BriefApplicationResponseDto } from '@/app/common/general-types/briefs.dto';
import { FormatService } from '@/app/common/format/format.service';
import { VideoResponseDto, VideoStatusEnum } from '@/app/common/general-types/videos.dto';
import { EmptyStateComponent } from '@/app/atoms/empty-state/empty-state.component';
import { VideoThumbnailComponent } from '@/app/atoms/video-thumbnail/video-thumbnail.component';
import { S3uploaderService } from '@/app/services/s3uploader/s3uploader.service';
import { VideoService } from '@/app/services/video/video.service';
import { CreatorActionPanel } from '../action-panel/action-panel';

@Component({
  selector: 'ins-creator-submit-content',
  imports: [CreatorActionPanel, EmptyStateComponent, VideoThumbnailComponent],
  templateUrl: './submit-content.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class CreatorSubmitContent implements OnInit {
  application = input.required<BriefApplicationResponseDto>();

  readonly videos = signal<VideoResponseDto[] | undefined>(undefined);
  readonly loadingState = signal<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  readonly expanded = model(false);
  readonly userVideoUploadRunning = signal(false);

  readonly uploadPercentage = inject(S3uploaderService).uploadPercentage;
  readonly formatService = inject(FormatService);
  readonly VideoStatusEnum = VideoStatusEnum;

  private destroyRef = inject(DestroyRef);
  private videoService = inject(VideoService);
  private uploadService = inject(S3uploaderService);

  ngOnInit(): void {
    this.loadVideos();
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const files = input.files;
    if (!files?.length) {
      return;
    }

    this.userVideoUploadRunning.set(true);
    try {
      await this.uploadService.uploadVideo(files, this.application());
      input.value = '';
      this.loadVideos();
    } finally {
      this.userVideoUploadRunning.set(false);
    }
  }

  onThumbnailPersisted(videoId: string, thumbnailURL: string): void {
    this.videos.update((list) =>
      (list ?? []).map((video) =>
        video.id === videoId ? { ...video, thumbnailURL } : video,
      ),
    );
  }

  uploadProgressWidth(): string {
    const percentage = this.uploadPercentage();
    return `width: ${percentage ?? 0}%`;
  }

  private loadVideos(): void {
    const app = this.application();
    this.loadingState.set('loading');

    this.videoService
      .getCreatorBriefVideos(app.creatorUserId, app.briefId)
      .pipe(
        catchError(() => {
          this.loadingState.set('error');
          return of([] as VideoResponseDto[]);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((videos) => {
        this.videos.set(videos);
        this.loadingState.set('loaded');
      });
  }
}
